import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
// import { selectRole } from '../../redux/slices/authSlice';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import AssignFacilityModal from './AssignFacilityModal';
import MultipleRoleSelector from './MultipleRoleSelector';
import PasswordResetDialog from './PasswordResetDialog';
import UserDeactivationDialog from './UserDeactivationDialog';
import UserManagementTable from './UserManagementTable';


// import { auth } from '../../firebase/config';
import { createUser, assignUserFacilities, getUsersForManagement } from '../../services/userService';
import { updateUserRoles, updateUserStatus } from '../../services/userManagementService';
import patientPortalService from '../../services/patientPortalService';
import { isAuthReady, waitForAuthReady } from '../../utils/authUtils';
// import { createAuditLog } from '../../redux/thunks/auditThunks';
import { RoleUtils, UserRole } from '../../types/enums';
import {
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  useTheme,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  OutlinedInput,
  ListItemText,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocale } from '../../context/LocaleContext';
import {
  Add as AddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { getAllTaxonomyCodes } from '../../services/taxonomyCodesService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));




const UserManagement = () => {
  const { t } = useLocale();
  const theme = useTheme();
  const userFacilities = useSelector(selectUserFacilities);
  // const dispatch = useDispatch();
  const { userRoles, defaultFacility } = useUserData();
  
  // Debug logging
  console.log('UserManagement component rendered with:', {
    userRole: userRoles,
    userFacilities: userFacilities?.length || 0,
    defaultFacility: defaultFacility?.id || 'none'
  });

  
  
  // Add ref to track component mount state
  const isMountedRef = useRef(true);
  
 
  
  // Memoize user data to prevent unnecessary re-renders
  const memoizedUserData = useMemo(() => {
    const data = {
      userRoles: userRoles,
      userFacilities: userFacilities || [],
      defaultFacility: defaultFacility?.id || ''
    }; 
    return data;
  }, [userRoles, userFacilities, defaultFacility?.id]);
  
  // State variables
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [facilityUser, setFacilityUser] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [deactivationUser, setDeactivationUser] = useState(null);
  const [deactivationOpen, setDeactivationOpen] = useState(false);
  const [rolesUpdate, setRolesUpdate] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null); 
  const [searchQuery, setSearchQuery] = useState('');
  const [useMultipleRoles, setUseMultipleRoles] = useState(false);
  const [taxonomySearchTerm, setTaxonomySearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 for regular users, 1 for patient portal users
  
  
  
  // New user and editing state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    roles: [],
    npi: '',
    phone: '',
    defaultFacility: defaultFacility?.id || '',
    twoFactorEnabled: false,
    taxonomyCode: '',
    assignedFacilities: []
  });

  const [editingUser, setEditingUser] = useState({
    name: '',
    email: '',
    roles: [],
    role: '',
    useMultipleRoles: false,
    npi: '',
    phone: '',
    defaultFacility: '',
    twoFactorEnabled: false,
    taxonomyCode: '',
    assignedFacilities: []
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [facilities, setFacilities] = useState([]);
  const [taxonomyCodes, setTaxonomyCodes] = useState([]);
  const [filteredTaxonomyCodes, setFilteredTaxonomyCodes] = useState([]);

  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingTaxonomyCodes, setFetchingTaxonomyCodes] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Check if user has access to this page
  const hasAccess = userRoles.includes(UserRole.ADMIN) || userRoles.includes(UserRole.FACILITY_ADMIN);
  


  // Helper function to get facility name from ID
  const getFacilityName = (facilityId) => {
    if (!facilityId) return '-';
    if (!Array.isArray(facilities)) {
      console.warn('facilities is not an array in getFacilityName:', facilities);
      return '-';
    }
    const facility = facilities.find(f => f.id === facilityId);
    return facility ? facility.name : '-';
  };

  // Filter and paginate users based on active tab
  const filteredUsers = users.filter(user => {
    const searchLower = (searchQuery || '').toLowerCase();
    
    // Handle new role structure
    let userRoles = user.roles || [];

    // Filter by user type based on active tab
    const isPatient = userRoles.includes('patient') || userRoles.includes('patient_portal');
    if (activeTab === 0 && isPatient) return false; // Regular users tab - exclude patients
    if (activeTab === 1 && !isPatient) return false; // Patient portal users tab - only show patients
    
    const roleText = RoleUtils.formatRolesForDisplay(userRoles)?.toLowerCase() || '';
    const facilityName = getFacilityName(user.defaultFacility)?.toLowerCase() || '';
    
    const matches = (
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.npi && user.npi.toLowerCase().includes(searchLower)) ||
      (user.taxonomyCode && user.taxonomyCode.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
      (facilityName && facilityName.includes(searchLower)) ||
      (roleText && roleText.includes(searchLower))
    );
    
    console.log('User filter result:', { user: user.name, matches, searchLower, roleText, facilityName });
    return matches;
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  


  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  // Single API call to get all necessary data
  const fetchAllData = useCallback(async () => {
 
    
    try {
      if (!isMountedRef.current) {
        console.log('fetchAllData: Component unmounted, returning early');
        return; // Prevent calls after unmount
      }
      
      if (fetchingUsers) {
        console.log('Data already being fetched, skipping...');
        return;
      }

      console.log('Starting fetchAllData...');
      setFetchingUsers(true);
      setError('');
      setLoading(true);

      // Single API call to get all data
      console.log('About to call getUsersForManagement()...');
    
      const result = await getUsersForManagement();
   
      
      if (!isMountedRef.current) return; // Check again after async call
      
     
      
      let usersArray = result?.users || [];
     
      
      // Log if we detect duplicates
      const userIds = usersArray.map(u => u.id);
      const duplicateIds = userIds.filter((id, index) => userIds.indexOf(id) !== index);
      if (duplicateIds.length > 0) {
        console.warn('Duplicate user IDs detected:', duplicateIds);
      }
      
     
      // Extract facilities from user objects (new structure)
      const allFacilities = new Map();
      
      usersArray.forEach(user => {
        // Extract facilities from user.facilities array
        if (user.facilities && Array.isArray(user.facilities)) {
          user.facilities.forEach(facility => {
            allFacilities.set(facility.id, facility);
          });
        }
      });
      
      // Set facilities from extracted data
      const facilityArray = Array.from(allFacilities.values());
      console.log('Facilities extracted from user objects, count:', facilityArray.length);
      setFacilities(facilityArray);
      
      // Use taxonomy codes from backend response if available
      if (result.taxonomyCodes) {
        console.log('Using taxonomy codes from backend response, count:', result.taxonomyCodes.length);
        setTaxonomyCodes(result.taxonomyCodes);
        setFilteredTaxonomyCodes(result.taxonomyCodes);
      } else {
        console.log('No taxonomy codes in backend response');
      }
      
      // Mark as loaded
      setInitialDataLoaded(true);
      setLoading(false);
      setUsers(usersArray);
      console.log('Initial data loaded flag set to true');
      
    } catch (error) {
      if (!isMountedRef.current) return; // Prevent state updates after unmount
      
      setError('Failed to fetch data' + error);
     
    } finally {
      if (isMountedRef.current) {
       
        setLoading(false);
        setFetchingUsers(false);
      }
    }
  }, [fetchingUsers, initialDataLoaded, loading]);
 
  // Only fetch taxonomy codes if not provided by main endpoint and component needs them
  const fetchTaxonomyCodes = useCallback(async () => {
    try {
      if (!isMountedRef.current) return; // Prevent calls after unmount
      
      if (fetchingTaxonomyCodes) {
        console.log('Taxonomy codes already being fetched, skipping...');
        return;
      }

      setFetchingTaxonomyCodes(true);
      const result = await getAllTaxonomyCodes();
      
      if (!isMountedRef.current) return; // Check again after async call
      
      // Extract the taxonomyCodes array from the result object
      const codes = result.taxonomyCodes || [];
      setTaxonomyCodes(codes);
      setFilteredTaxonomyCodes(codes);
    } catch (error) {
      if (!isMountedRef.current) return; // Prevent state updates after unmount
      console.error('Error fetching taxonomy codes:', error);
    } finally {
      if (isMountedRef.current) {
        setFetchingTaxonomyCodes(false);
      }
    }
  }, [fetchingTaxonomyCodes]);

  // Taxonomy search functionality
  const handleTaxonomySearch = async (searchTerm, isEditing = false) => {
    if (searchTerm.length === 0) {
      setFilteredTaxonomyCodes(taxonomyCodes);
      return;
    }
    
    try {
      // For now, use client-side filtering since we don't have searchTaxonomyCodes
      const filtered = taxonomyCodes.filter(code => 
        (code.taxonomy_code && code.taxonomy_code.toLowerCase().includes((searchTerm || '').toLowerCase())) ||
        (code.name && code.name.toLowerCase().includes((searchTerm || '').toLowerCase()))
      );
      setFilteredTaxonomyCodes(filtered);
    } catch (error) {
      console.error('Error searching taxonomy codes:', error);
      // Fallback to client-side filtering
      const filtered = taxonomyCodes.filter(code => 
        (code.taxonomy_code && code.taxonomy_code.toLowerCase().includes((searchTerm || '').toLowerCase())) ||
        (code.name && code.name.toLowerCase().includes((searchTerm || '').toLowerCase()))
      );
      setFilteredTaxonomyCodes(filtered);
    }
  }; 

  // User creation functionality
  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!userRoles.includes(UserRole.ADMIN)) {
      setError('Only administrators can add new users');
      setLoading(false);
      return;
    }

    // Validate required fields
    if (newUser.assignedFacilities.length === 0) {
      setError('At least one facility must be assigned');
      setLoading(false);
      return;
    }
    
    // If multiple facilities are assigned, default facility is required
    if (newUser.assignedFacilities.length > 1 && !newUser.defaultFacility) {
      setError('Default facility is required when multiple facilities are assigned');
      setLoading(false);
      return;
    }

    try {
      // Prepare user data based on whether multiple roles are used
      const userData = useMultipleRoles && newUser.roles.length > 0 ? {
        ...newUser,
        role: newUser.roles[0], // Set primary role for backward compatibility
        roles: newUser.roles,
        // If only one facility is assigned, it becomes the default
        defaultFacility: newUser.assignedFacilities.length === 1 ? newUser.assignedFacilities[0] : newUser.defaultFacility
      } : {
        ...newUser,
        roles: [newUser.role], // Convert single role to array for consistency
        // If only one facility is assigned, it becomes the default
        defaultFacility: newUser.assignedFacilities.length === 1 ? newUser.assignedFacilities[0] : newUser.defaultFacility
      };

      const result = await createUser(userData);

      // If user was created successfully and has facility assignments, assign them
      if (result.user && newUser.assignedFacilities.length > 0) {
        try {
          console.log('Assigning facilities to new user:', newUser.assignedFacilities);
          await assignUserFacilities(result.user.uid, newUser.assignedFacilities);
        } catch (facilityError) {
          console.error('Error assigning facilities to new user:', facilityError);
          // Don't fail user creation if facility assignment fails
        }
      }

      // Add the new user to the local state immediately
      if (result.user) {
        const newUserData = {
          ...result.user,
          // Ensure the user has the correct structure expected by the table
          id: result.user.uid,
          uid: result.user.uid,
          facilities: result.user.assignedFacilities || [],
          facilityIds: result.user.assignedFacilities?.map(f => f.id) || [],
          // Ensure required fields have default values
          status: result.user.status || 'active',
          createdAt: result.user.createdAt || new Date().toISOString(),
          updatedAt: result.user.updatedAt || new Date().toISOString()
        };
        
        console.log('Adding new user to local state:', newUserData);
        
        // Add to the beginning of the users array
        setUsers(prevUsers => [newUserData, ...prevUsers]);
      }
      
      // Reset form
      setNewUser({
        email: '',
        password: '',
        name: '',
        role: 'user',
        roles: [],
        npi: '',
        phone: '',
        defaultFacility: defaultFacility?.id || '',
        twoFactorEnabled: false,
        taxonomyCode: '',
        assignedFacilities: []
      });
      setShowAddUser(false);
      setUseMultipleRoles(false);
      
      // Also refresh from backend to ensure consistency
      console.log('Refreshing data from backend...');
      await fetchAllData();
      console.log('Backend refresh completed. Current users count:', users.length);
      
      setError('');
    } catch (error) {
      console.error('Error adding user:', error);
      setError(error instanceof Error ? error.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };




  // Main useEffect for fetching data when role and facilities are available
  useEffect(() => {
    const initializeData = async () => {
      if (!memoizedUserData.userRoles || initialDataLoaded) {
        return; // Wait for role to be loaded and prevent repeated calls
      }
      
      // Wait for authentication to be ready before making API calls
      if (!isAuthReady()) {
        console.log('UserManagement: Auth not ready, waiting...');
        const authReady = await waitForAuthReady(5000); // Wait up to 5 seconds
        if (!authReady) {
          console.error('UserManagement: Authentication not ready after timeout');
          setError('Authentication not ready. Please refresh the page and try again.');
          return;
        }
      }
      
      console.log('About to call fetchAllData...');
      
      // For now, let's bypass the debouncing to see if that's the issue
      if (memoizedUserData.userRoles.includes(UserRole.FACILITY_ADMIN)) {
        // For facility_admin, wait for userFacilities to be loaded
        if (memoizedUserData.userFacilities && memoizedUserData.userFacilities.length > 0) {
          console.log('Calling fetchAllData for facility_admin');
          fetchAllData();
          setInitialDataLoaded(true);
        } else {
          console.log('Facility admin has no facilities assigned');
        }
      } else if (memoizedUserData.userRoles.includes(UserRole.ADMIN)) {
        // For admin, fetch data immediately
        console.log('Calling fetchAllData for admin');
        fetchAllData();
        setInitialDataLoaded(true);
      }
    };
    
    initializeData();
  }, [memoizedUserData, initialDataLoaded, fetchAllData]); // Added fetchAllData to dependencies

  // Only fetch taxonomy codes if not provided by main endpoint and component needs them
  useEffect(() => {
    if (memoizedUserData.userRoles && !initialDataLoaded && taxonomyCodes.length === 0) {
      fetchTaxonomyCodes();
    }
  }, [memoizedUserData.userRoles, initialDataLoaded, fetchTaxonomyCodes, taxonomyCodes.length]);

  // Handle default facility logic for new user form
  useEffect(() => {
    if (newUser.assignedFacilities.length === 1) {
      // If only one facility is assigned, automatically set it as default
      setNewUser(prev => ({ ...prev, defaultFacility: newUser.assignedFacilities[0] }));
    } else if (newUser.assignedFacilities.length === 0) {
      // If no facilities are assigned, clear the default facility
      setNewUser(prev => ({ ...prev, defaultFacility: '' }));
    }
  }, [newUser.assignedFacilities]);

  // Handle default facility logic for editing user form
  useEffect(() => {
    if (editingUser.assignedFacilities.length === 1) {
      // If only one facility is assigned, automatically set it as default
      setEditingUser(prev => ({ ...prev, defaultFacility: editingUser.assignedFacilities[0] }));
    } else if (editingUser.assignedFacilities.length === 0) {
      // If no facilities are assigned, clear the default facility
      setEditingUser(prev => ({ ...prev, defaultFacility: '' }));
    }
  }, [editingUser.assignedFacilities]);



  



  // Role update handler
  const handleRoleUpdate = async (userId, newRoles) => {
    if (!userRoles.includes(UserRole.ADMIN)) {
      setError('Only administrators can update user roles');
      return;
    }
    try {
      console.log('handleRoleUpdate called with secure server function:', { userId, newRoles });
      
     
      // Convert single role to array for consistency
      const rolesArray = Array.isArray(newRoles) ? newRoles : [newRoles];

      // Use secure server-side function instead of direct Firestore writes
      const result = await updateUserRoles(userId, rolesArray);
      
     

      // Update local state with server response
      setUsers(users.map(user =>
        user.updatedUser.id === userId ? { 
          ...user.updatedUser, 
          roles: result.updatedUser.roles,
          role: result.updatedUser.roles // Update role field to contain the entire roles array
        } : user
      ));
      setSelectedUser(null);
      setRolesUpdate([]);
      
      // Clear any previous errors
      setError('');
    } catch (error) {
      console.error('Error updating roles:', error);
      
      // Handle specific error types
      if (error.message.includes('permission-denied')) {
        setError('You do not have permission to update user roles');
      } else if (error.message.includes('not-found')) {
        setError('User not found');
      } else if (error.message.includes('invalid-argument')) {
        setError('Invalid role data provided');
      } else {
        setError('Failed to update roles');
      }
    }
  };

  // Start editing roles
  const startEditingRoles = (user) => {
    setSelectedUser(user);
    
    // Handle new role structure
    let userRoles = user.roles || [];  
    
    setRolesUpdate(userRoles);
  };

  // Start editing user
  const startEditingUser = async (user) => {
    try {
      // Use facility assignments from user object (new structure)
      const userFacilityAssignmentsList = user.facilityIds || [];
      
      // Handle new role structure
      let userRoles;
      let useMultipleRoles = false;
      let legacyRole = '';
      
      if (user.role && Array.isArray(user.role)) {
        // New structure: role field contains the entire roles array
        userRoles = user.role;
        useMultipleRoles = true;
      } else if (user.roles && Array.isArray(user.roles)) {
        // Fallback to roles field if role is not an array
        userRoles = user.roles;
        useMultipleRoles = true;
      } else if (user.role && !Array.isArray(user.role)) {
        // Legacy structure: single role
        userRoles = [];
        legacyRole = user.role;
        useMultipleRoles = false;
      } else {
        userRoles = [];
        useMultipleRoles = false;
      }
      
      setEditingUser({
        name: user.name || '',
        email: user.email || '',
        roles: userRoles,
        role: legacyRole,
        useMultipleRoles: useMultipleRoles,
        npi: user.npi || '',
        phone: user.phone || '',
        defaultFacility: user.defaultFacility || '',
        twoFactorEnabled: user.twoFactorEnabled || false,
        taxonomyCode: user.taxonomyCode || '',
        assignedFacilities: userFacilityAssignmentsList
      });
      setShowEditUser(user.id);
      setTaxonomySearchTerm('');
      setFilteredTaxonomyCodes(taxonomyCodes);
      
      // Scroll to top of page to show the edit form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error setting up user editing:', error);
      // Set without facility assignments if there's an error
      setEditingUser({
        name: user.name || '',
        email: user.email || '',
        roles: [],
        role: user.role || '',
        useMultipleRoles: false,
        npi: user.npi || '',
        phone: user.phone || '',
        defaultFacility: user.defaultFacility || '',
        twoFactorEnabled: user.twoFactorEnabled || false,
        taxonomyCode: user.taxonomyCode || '',
        assignedFacilities: []
      });
      setShowEditUser(user.id);
      setTaxonomySearchTerm('');
      setFilteredTaxonomyCodes(taxonomyCodes);
      
      // Scroll to top of page to show the edit form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Password reset handlers
  const handlePasswordResetClick = (user) => {
    setPasswordResetUser(user);
    setPasswordResetOpen(true);
  };

  const handlePasswordResetClose = () => {
    setPasswordResetOpen(false);
    setPasswordResetUser(null);
  };

  const handlePasswordResetSuccess = () => {
    setError('');
    setTimeout(() => {
      handlePasswordResetClose();
    }, 3000);
  };

  // User deactivation handlers
  const handleDeactivationClick = (user) => {
    setDeactivationUser(user);
    setDeactivationOpen(true);
  };

  const handleDeactivationClose = () => {
    setDeactivationOpen(false);
    setDeactivationUser(null);
  };

  const handleDeactivationSuccess = async (userId, isDeactivating, reason) => {
    try {
      const newStatus = isDeactivating ? 'inactive' : 'active';
      
      // Find the user being deactivated to check if it's a patient portal user
      const userToDeactivate = users.find(user => user.id === userId);
      const isPatientPortalUser = userToDeactivate?.roles?.includes('patient') || userToDeactivate?.roles?.includes('patient_portal');
      
      if (isPatientPortalUser) {
        // For patient portal users, use the patient portal service to properly manage portal access
        const patientId = userToDeactivate.patientId;
        if (patientId) {
          if (isDeactivating) {
            // Deactivating: Use the same service methods as the patient details page for consistency
            console.log('Deactivating patient portal user, using patient portal service');
            
            if (userToDeactivate.firebaseUid) {
              // Delete Firebase Auth user and clean up (same as patient details page)
              await patientPortalService.disablePatientPortal(patientId, userToDeactivate.firebaseUid);
            }
            
            // Update patient portal status (same as patient details page)
            await patientPortalService.updatePatientPortalStatus(patientId, false);
          } else {
            // Reactivating: Re-enable patient portal access
            console.log('Reactivating patient portal user, using patient portal service');
            
            // Update patient portal status to enabled
            await patientPortalService.updatePatientPortalStatus(patientId, true, userToDeactivate.firebaseUid);
          }
          
          // Also update the user status to maintain consistency
          await updateUserStatus(userId, newStatus, reason);
        } else {
          console.warn('Patient portal user has no patientId, falling back to regular user status update');
          await updateUserStatus(userId, newStatus, reason);
        }
      } else {
        // For regular users, use the standard user status update
        await updateUserStatus(userId, newStatus, reason);
      }
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { 
                ...user, 
                status: newStatus,
                deactivationReason: reason || null
              }
            : user
        )
      );
      
      setError('');
      // Show success message
      console.log(`User ${isDeactivating ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  };

  // If user doesn't have access, show access denied
  if (!hasAccess) {
    console.log('Access denied - showing access denied message');
    console.log('Access denied - userRole:', userRoles);
    console.log('Access denied - UserRole.ADMIN:', UserRole.ADMIN);
    console.log('Access denied - UserRole.FACILITY_ADMIN:', UserRole.FACILITY_ADMIN);
    console.log('Access denied - hasAccess:', hasAccess);
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <ModernCard>
          <CardContent>
            <Alert severity="error">
              Access denied. Admin or facility admin access required.
            </Alert>
          </CardContent>
        </ModernCard>
      </Box>
    );
  }




  if (loading) {
   
   
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh',
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      '& *': {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif !important',
      }
    }}>
      {/* Test message to see if component is rendering */}
      
      
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {t('userManagement.title')}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage system users, roles, and permissions
              </Typography>
            </Box>
            {userRoles.includes(UserRole.ADMIN) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddUser(!showAddUser)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                }}
              >
                {t('userManagement.addUser')}
              </Button>
            )}
          </Box>
        </CardContent>
      </HeaderCard>

     

      {error && (
        <ModernCard>
          <CardContent>
            <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
          </CardContent>
        </ModernCard>
      )}

      {showAddUser && (
        <ModernCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
              Add New User
            </Typography>
            <form onSubmit={handleAddUser}>
              <Stack spacing={1.5}>
                <TextField
                  label="Name"
                  value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={useMultipleRoles}
                      onChange={(e) => {
                        setUseMultipleRoles(e.target.checked);
                        if (!e.target.checked) {
                          setNewUser({ ...newUser, roles: [] });
                        }
                      }}
                    />
                  }
                  label="Enable Multiple Roles"
                />

                {useMultipleRoles ? (
                  <MultipleRoleSelector
                    selectedRoles={newUser.roles}
                    onChange={(roles) => setNewUser({ ...newUser, roles })}
                    label="Role"
                    helperText="Select one or more roles for this user"
                  />
                ) : (
                  <FormControl size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={newUser.role}
                      onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                      label="Role"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '8px',
                          fontSize: '0.875rem'
                        } 
                      }}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="nurse">Nurse</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="facility_admin">Facility Admin</MenuItem>
                      <MenuItem value="billing">Billing</MenuItem>
                      <MenuItem value="patient">Patient</MenuItem>
                      <MenuItem value="patient_portal">Patient Portal</MenuItem>
                    </Select>
                  </FormControl>
                )}
                
                <TextField
                  label="NPI (National Provider Identifier)"
                  value={newUser.npi}
                  onChange={e => setNewUser({ ...newUser, npi: e.target.value })}
                  placeholder="Enter 10-digit NPI number"
                  inputProps={{ maxLength: 10 }}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                <FormControl fullWidth>
                  <Autocomplete
                    options={filteredTaxonomyCodes}
                    getOptionLabel={(option) => option.taxonomy_code ? `${option.taxonomy_code} - ${option.name}` : ''}
                    value={taxonomyCodes.find(code => code.taxonomy_code === newUser.taxonomyCode) || null}
                    onChange={(event, newValue) => {
                      setNewUser({ ...newUser, taxonomyCode: newValue ? newValue.taxonomy_code : '' });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setTaxonomySearchTerm(newInputValue);
                      handleTaxonomySearch(newInputValue);
                    }}
                    inputValue={taxonomySearchTerm}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Taxonomy Code"
                        placeholder="Search taxonomy codes..."
                        size="small"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: '8px',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.taxonomy_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    filterOptions={(x) => x}
                    noOptionsText="No taxonomy codes found"
                    loading={false}
                  />
                </FormControl>
                
                <TextField
                  label="Phone Number"
                  value={newUser.phone}
                  onChange={e => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="Enter phone number"
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                {/* Only show default facility when there are multiple assigned facilities */}
                {newUser.assignedFacilities.length > 1 && (
                  <FormControl required size="small">
                    <InputLabel>Default Facility</InputLabel>
                    <Select
                      value={newUser.defaultFacility}
                      onChange={e => setNewUser({ ...newUser, defaultFacility: e.target.value })}
                      label="Default Facility"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '8px',
                          fontSize: '0.875rem'
                        } 
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a default facility</em>
                      </MenuItem>
                      {newUser.assignedFacilities.map((facilityId) => {
                        const facility = facilities.find(f => f.id === facilityId);
                        return facility ? (
                          <MenuItem key={facility.id} value={facility.id}>
                            {facility.name}
                          </MenuItem>
                        ) : null;
                      })}
                    </Select>
                  </FormControl>
                )}
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={newUser.twoFactorEnabled}
                      onChange={(e) => setNewUser({ ...newUser, twoFactorEnabled: e.target.checked })}
                    />
                  }
                  label="Two-Factor Authentication Enabled"
                />
                
                <FormControl fullWidth size="small" required error={newUser.assignedFacilities.length === 0}>
                  <InputLabel>Assign Facilities *</InputLabel>
                  <Select
                    multiple
                    value={newUser.assignedFacilities}
                    onChange={(e) => setNewUser({ ...newUser, assignedFacilities: e.target.value })}
                    input={<OutlinedInput label="Assign Facilities *" size="small" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value, index) => {
                          const facility = Array.isArray(facilities) ? facilities.find(f => f.id === value) : null;
                          return (
                            <Chip 
                              key={`newuser-facility-${value}-${index}`} 
                              label={facility ? facility.name : value} 
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.dark,
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '8px',
                        fontSize: '0.875rem'
                      },
                      '& .MuiSelect-select': {
                        minHeight: 'auto',
                        padding: '8px 12px'
                      },
                      // Force consistent text color in the dropdown
                      '& .MuiMenu-root .MuiMenuItem-root': {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiMenu-root .MuiMenuItem-root.Mui-selected': {
                        color: theme.palette.text.primary,
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            }
                          },
                          // Force text color to stay consistent regardless of selection state
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            }
                          },
                          '& .MuiCheckbox-root': {
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            }
                          },
                          '& .MuiListItemText-primary': {
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          },
                          // Override any Material-UI default text color changes
                          '& .MuiMenuItem-root.Mui-selected .MuiListItemText-primary': {
                            color: theme.palette.text.primary,
                          },
                          '& .MuiMenuItem-root.Mui-selected .MuiTypography-root': {
                            color: theme.palette.text.primary,
                          }
                        }
                      }
                    }}
                  >
                    {Array.isArray(facilities) && facilities.map((facility) => (
                      <MenuItem 
                        key={facility.id} 
                        value={facility.id}
                      >
                        <Checkbox 
                          checked={newUser.assignedFacilities.includes(facility.id)}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            }
                          }}
                        />
                        <ListItemText 
                          primary={facility.name}
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            // Force text color to stay consistent
                            '& .MuiTypography-root': {
                              color: theme.palette.text.primary,
                            }
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                  <Button 
                    type="submit" 
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  >
                    Save
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowAddUser(false);
                      setUseMultipleRoles(false);
                      setNewUser({ 
                        email: '', 
                        password: '', 
                        name: '', 
                        role: 'user', 
                        roles: [], 
                        npi: '', 
                        phone: '', 
                        defaultFacility: defaultFacility?.id || '', 
                        twoFactorEnabled: false, 
                        taxonomyCode: '', 
                        assignedFacilities: [] 
                      });
                      setTaxonomySearchTerm('');
                      setFilteredTaxonomyCodes(taxonomyCodes);
                    }}
                    size="small"
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </ModernCard>
      )}

      {/* Edit User Form */}
      {showEditUser && (
        <ModernCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
              Edit User: {editingUser.name}
            </Typography>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              
              // Validate required fields
              if (editingUser.assignedFacilities.length === 0) {
                setError('At least one facility must be assigned');
                setLoading(false);
                return;
              }
              
              // If multiple facilities are assigned, default facility is required
              if (editingUser.assignedFacilities.length > 1 && !editingUser.defaultFacility) {
                setError('Default facility is required when multiple facilities are assigned');
                setLoading(false);
                return;
              }
              
              try {
                // Find the user being edited
                const userToUpdate = users.find(u => u.id === showEditUser);
                if (!userToUpdate) {
                  setError('User not found');
                  setLoading(false);
                  return;
                }

                // Calculate the default facility
                const calculatedDefaultFacility = editingUser.assignedFacilities.length === 1 
                  ? editingUser.assignedFacilities[0] 
                  : editingUser.defaultFacility;

                // Prepare profile data for backend update (exclude roles - they use a separate endpoint)
                const profileData = {
                  name: editingUser.name,
                  email: editingUser.email,
                  npi: editingUser.npi,
                  phone: editingUser.phone,
                  defaultFacility: calculatedDefaultFacility,
                  twoFactorEnabled: editingUser.twoFactorEnabled,
                  taxonomyCode: editingUser.taxonomyCode
                };

                // Prepare roles for update
                const newRoles = editingUser.useMultipleRoles 
                  ? editingUser.roles 
                  : [editingUser.role].filter(Boolean);

                // Update user profile in backend (non-role fields)
                const { updateUserProfile } = await import('../../services/userService');
                await updateUserProfile(showEditUser, profileData);

                // Update roles using the roles endpoint
                if (newRoles.length > 0) {
                  await updateUserRoles(showEditUser, newRoles);
                }

                // Update facility assignments in user_facilities collection
                await assignUserFacilities(showEditUser, editingUser.assignedFacilities);

                // Update local state with the changes
                const updatedUser = {
                  ...userToUpdate,
                  ...profileData,
                  roles: newRoles,
                  role: newRoles[0] || userToUpdate.role, // Keep backward compatibility
                  facilityIds: editingUser.assignedFacilities,
                  facilities: editingUser.assignedFacilities.map(facilityId => {
                    const facility = facilities.find(f => f.id === facilityId);
                    return facility || { id: facilityId, name: 'Unknown Facility' };
                  })
                };

                setUsers(prevUsers => 
                  prevUsers.map(user => 
                    user.id === showEditUser ? updatedUser : user
                  )
                );

                // Close edit form
                setShowEditUser(null);
                setEditingUser({
                  name: '',
                  email: '',
                  roles: [],
                  role: '',
                  useMultipleRoles: false,
                  npi: '',
                  phone: '',
                  defaultFacility: '',
                  twoFactorEnabled: false,
                  taxonomyCode: '',
                  assignedFacilities: []
                });

                setError('');
              } catch (error) {
                console.error('Error updating user:', error);
                setError('Failed to update user: ' + (error.message || 'Unknown error'));
              } finally {
                setLoading(false);
              }
            }}>
              <Stack spacing={1.5}>
                <TextField
                  label="Name"
                  value={editingUser.name}
                  onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={editingUser.email}
                  onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                  required
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingUser.useMultipleRoles}
                      onChange={(e) => setEditingUser({ ...editingUser, useMultipleRoles: e.target.checked })}
                    />
                  }
                  label="Enable Multiple Roles"
                />

                {editingUser.useMultipleRoles ? (
                  <MultipleRoleSelector
                    selectedRoles={editingUser.roles}
                    onChange={(roles) => setEditingUser({ ...editingUser, roles })}
                    label="Roles"
                    helperText="Select one or more roles for this user"
                    showLegacyWarning={editingUser.role && !editingUser.roles.length}
                  />
                ) : (
                  <FormControl size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={editingUser.role || ''}
                      onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                      label="Role"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '8px',
                          fontSize: '0.875rem'
                        } 
                      }}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="nurse">Nurse</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="facility_admin">Facility Admin</MenuItem>
                      <MenuItem value="billing">Billing</MenuItem>
                      <MenuItem value="patient">Patient</MenuItem>
                      <MenuItem value="patient_portal">Patient Portal</MenuItem>
                    </Select>
                                     </FormControl>
                )}
                
                <TextField
                  label="NPI (National Provider Identifier)"
                  value={editingUser.npi}
                  onChange={e => setEditingUser({ ...editingUser, npi: e.target.value })}
                  placeholder="Enter 10-digit NPI number"
                  inputProps={{ maxLength: 10 }}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                <FormControl fullWidth>
                  <Autocomplete
                    options={filteredTaxonomyCodes}
                    getOptionLabel={(option) => option.taxonomy_code ? `${option.taxonomy_code} - ${option.name}` : ''}
                    value={taxonomyCodes.find(code => code.taxonomy_code === editingUser.taxonomyCode) || null}
                    onChange={(event, newValue) => {
                      setEditingUser({ ...editingUser, taxonomyCode: newValue ? newValue.taxonomy_code : '' });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setTaxonomySearchTerm(newInputValue);
                      handleTaxonomySearch(newInputValue);
                    }}
                    inputValue={taxonomySearchTerm}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Taxonomy Code"
                        placeholder="Search taxonomy codes..."
                        size="small"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: '8px',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.taxonomy_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    filterOptions={(x) => x}
                    noOptionsText="No taxonomy codes found"
                    loading={false}
                  />
                </FormControl>
                
                <TextField
                  label="Phone Number"
                  value={editingUser.phone}
                  onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })}
                  placeholder="Enter phone number"
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    } 
                  }}
                />
                
                <FormControl fullWidth size="small" required error={editingUser.assignedFacilities.length === 0}>
                  <InputLabel>Assign Facilities *</InputLabel>
                  <Select
                    multiple
                    value={editingUser.assignedFacilities}
                    onChange={(e) => setEditingUser({ ...editingUser, assignedFacilities: e.target.value })}
                    input={<OutlinedInput label="Assign Facilities *" size="small" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value, index) => {
                          const facility = Array.isArray(facilities) ? facilities.find(f => f.id === value) : null;
                          return (
                            <Chip 
                              key={`editinguser-facility-${value}-${index}`} 
                              label={facility ? facility.name : value} 
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.dark,
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '8px',
                        fontSize: '0.875rem'
                      },
                      '& .MuiSelect-select': {
                        minHeight: 'auto',
                        padding: '8px 12px'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            }
                          },
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            }
                          },
                          '& .MuiCheckbox-root': {
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            }
                          },
                          '& .MuiListItemText-primary': {
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }
                        }
                      }
                    }}
                  >
                    {Array.isArray(facilities) && facilities.map((facility) => (
                      <MenuItem 
                        key={facility.id} 
                        value={facility.id}
                      >
                        <Checkbox 
                          checked={editingUser.assignedFacilities.includes(facility.id)}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            }
                          }}
                        />
                        <ListItemText 
                          primary={facility.name}
                          sx={{
                            color: theme.palette.text.primary,
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Only show default facility when there are multiple assigned facilities */}
                {editingUser.assignedFacilities.length > 1 && (
                  <FormControl size="small">
                    <InputLabel>Default Facility</InputLabel>
                    <Select
                      value={editingUser.defaultFacility}
                      onChange={e => setEditingUser({ ...editingUser, defaultFacility: e.target.value })}
                      label="Default Facility"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '8px',
                          fontSize: '0.875rem'
                        } 
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a default facility</em>
                      </MenuItem>
                      {editingUser.assignedFacilities.map((facilityId) => {
                        const facility = facilities.find(f => f.id === facilityId);
                        return facility ? (
                          <MenuItem key={facility.id} value={facility.id}>
                            {facility.name}
                          </MenuItem>
                        ) : null;
                      })}
                    </Select>
                  </FormControl>
                )}
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingUser.twoFactorEnabled}
                      onChange={(e) => setEditingUser({ ...editingUser, twoFactorEnabled: e.target.checked })}
                    />
                  }
                  label="Two-Factor Authentication Enabled"
                />
                
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                  <Button 
                    type="submit" 
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  >
                    Update User
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowEditUser(null);
                      setEditingUser({
                        name: '',
                        email: '',
                        roles: [],
                        role: '',
                        useMultipleRoles: false,
                        npi: '',
                        phone: '',
                        defaultFacility: '',
                        twoFactorEnabled: false,
                        taxonomyCode: '',
                        assignedFacilities: []
                      });
                      setTaxonomySearchTerm('');
                      setFilteredTaxonomyCodes(taxonomyCodes);
                    }}
                    size="small"
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </ModernCard>
      )}

      <ModernCard>
        <CardContent>
          {/* User Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  minHeight: '48px',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                  height: '3px',
                }
              }}
            >
              <Tab 
                icon={<PeopleIcon />} 
                iconPosition="start"
                label="Regular Users" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1
                }}
              />
              <Tab 
                icon={<PersonIcon />} 
                iconPosition="start"
                label="Patient Portal Users" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Content Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
              {activeTab === 0 ? 'Regular Users' : 'Patient Portal Users'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {activeTab === 0 
                ? 'Manage healthcare staff, administrators, and other system users'
                : 'Manage patient accounts and portal access'
              }
            </Typography>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('userManagement.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': { 
                borderRadius: '12px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <UserManagementTable
            users={users}
            filteredUsers={filteredUsers}
            paginatedUsers={paginatedUsers}
            page={page}
            rowsPerPage={rowsPerPage}
            searchQuery={searchQuery}
            selectedUser={selectedUser}
            rolesUpdate={rolesUpdate}
            setRolesUpdate={setRolesUpdate}
            editingUser={editingUser}
            showEditUser={showEditUser}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onRoleUpdate={handleRoleUpdate}
            onStartEditingRoles={startEditingRoles}
            onStartEditingUser={startEditingUser}
            onAssignFacility={(user) => {
              setFacilityUser(user);
              setModalOpen(true);
            }}
            onPasswordReset={handlePasswordResetClick}
            onDeactivation={handleDeactivationClick}
            getFacilityName={getFacilityName}
            userRole={userRoles}
            theme={theme}
            onCancelEditRoles={() => {
              setSelectedUser(null);
              setRolesUpdate([]);
            }}
            activeTab={activeTab}
          />
        </CardContent>
      </ModernCard>
        
        {modalOpen && (
          <AssignFacilityModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            user={facilityUser}
            currentUserRole={userRoles}
            userFacilities={userFacilities}
          />
        )}

        {passwordResetOpen && (
          <PasswordResetDialog
            open={passwordResetOpen}
            onClose={handlePasswordResetClose}
            user={passwordResetUser}
            onSuccess={handlePasswordResetSuccess}
          />
        )}

        {deactivationOpen && (
          <UserDeactivationDialog
            open={deactivationOpen}
            onClose={handleDeactivationClose}
            user={deactivationUser}
            onSuccess={handleDeactivationSuccess}
            isDeactivating={deactivationUser?.status !== 'inactive'}
          />
        )}
      </Box>
    );
  };

  export default UserManagement;
